import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Plus, PencilSimple, Key, Copy, ArrowClockwise, Warning, Check, Bank, Export } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ModulePermissionsEditor from '../components/ModulePermissionsEditor';
import { usePermissions } from '../hooks/usePermissions';

const UsersPage = () => {
  const { axiosInstance } = useAuth();
  const { canManage } = usePermissions();
  const canEditUsers = canManage('usuarios');
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showTempPasswordModal, setShowTempPasswordModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [passwordUser, setPasswordUser] = useState(null);
  const [tempPasswordResult, setTempPasswordResult] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    second_name: '',
    last_name: '',
    second_last_name: '',
    role: 'encargado',
    branch_id: '',
    can_manage_bank_accounts: false,
    allowed_branches: [],
  });
  const [newPassword, setNewPassword] = useState('');
  const [forceChange, setForceChange] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [allActiveBranches, setAllActiveBranches] = useState([]);
  const [roleTemplates, setRoleTemplates] = useState({});
  const [isTemplateApplied, setIsTemplateApplied] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [usersRes, branchesRes, allBranchesRes, templatesRes] = await Promise.all([
        axiosInstance.get('/api/users'),
        axiosInstance.get('/api/branches'),
        axiosInstance.get('/api/branches/all-active'),
        axiosInstance.get('/api/permissions/role-templates'),
      ]);
      setUsers(usersRes.data);
      setBranches(branchesRes.data);
      setAllActiveBranches(allBranchesRes.data);
      setRoleTemplates(templatesRes.data.templates || {});
    } catch (error) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleOpenModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        email: user.email,
        password: '',
        first_name: user.first_name || user.name?.split(' ')[0] || '',
        second_name: user.second_name || '',
        last_name: user.last_name || user.name?.split(' ').slice(1).join(' ') || '',
        second_last_name: user.second_last_name || '',
        role: user.role,
        branch_id: user.branch_id || '',
        can_manage_bank_accounts: user.can_manage_bank_accounts || false,
        can_manage_cierre_mes: user.can_manage_cierre_mes || false,
        allowed_branches: user.allowed_branches || [],
        module_permissions: user.module_permissions || roleTemplates[user.role] || {},
      });
      setIsTemplateApplied(!user.module_permissions || Object.keys(user.module_permissions).length === 0);
    } else {
      const defaultPerms = roleTemplates['encargado'] || {};
      setEditingUser(null);
      setFormData({
        email: '',
        password: '',
        first_name: '',
        second_name: '',
        last_name: '',
        second_last_name: '',
        role: 'encargado',
        branch_id: branches[0]?.id || '',
        can_manage_bank_accounts: false,
        can_manage_cierre_mes: false,
        allowed_branches: [],
        module_permissions: defaultPerms,
      });
      setIsTemplateApplied(true);
    }
    setError('');
    setShowModal(true);
  };

  const handleRoleChange = (newRole) => {
    if (editingUser && formData.module_permissions && Object.keys(formData.module_permissions).length > 0) {
      const apply = window.confirm('Cambiar el rol puede reemplazar los permisos personalizados actuales por la plantilla sugerida del nuevo rol. ¿Deseas continuar?');
      if (!apply) return;
    }
    const template = roleTemplates[newRole] || {};
    setFormData({ ...formData, role: newRole, module_permissions: template });
    setIsTemplateApplied(true);
  };

  const handlePermissionsChange = (newPerms) => {
    setFormData({ ...formData, module_permissions: newPerms });
    setIsTemplateApplied(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      if (editingUser) {
        const updateData = { ...formData };
        delete updateData.password;
        await axiosInstance.put(`/api/users/${editingUser.id}`, updateData);
      } else {
        await axiosInstance.post('/api/users', formData);
      }
      setShowModal(false);
      loadData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setError('');
    setSaving(true);

    try {
      await axiosInstance.put(`/api/users/${passwordUser.id}/password`, {
        new_password: newPassword,
        force_change: forceChange,
      });
      setShowPasswordModal(false);
      setNewPassword('');
      setForceChange(true);
      setPasswordUser(null);
      toast.success('Contraseña actualizada correctamente');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateTempPassword = async (user) => {
    setSaving(true);
    try {
      const res = await axiosInstance.post(`/api/users/${user.id}/generate-temp-password`);
      setTempPasswordResult(res.data);
      setShowTempPasswordModal(true);
      toast.success('Contraseña temporal generada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al generar contraseña temporal');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('Copiado al portapapeles');
    });
  };

  const handleToggleActive = async (user) => {
    try {
      await axiosInstance.put(`/api/users/${user.id}`, { is_active: !user.is_active });
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const getBranchName = (branchId) => {
    const branch = branches.find((b) => b.id === branchId);
    return branch?.name || '-';
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-900" data-testid="users-title">
            Usuarios
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Administración de usuarios del sistema
          </p>
        </div>
        {canEditUsers && (
        <button
          onClick={() => handleOpenModal()}
          className="btn-primary flex items-center gap-2"
          data-testid="add-user-btn"
        >
          <Plus size={18} />
          Nuevo Usuario
        </button>
        )}
      </div>

      {/* Users Table */}
      <div className="table-container">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Nombre
                  </th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Email
                  </th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Rol
                  </th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Sucursal
                  </th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Estado
                  </th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Permisos
                  </th>
                  {canEditUsers && (
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Acciones
                  </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="data-row border-b border-zinc-100">
                    <td className="px-4 py-3 text-sm font-medium text-zinc-900">
                      {user.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-600">
                      {user.email}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={user.role === 'admin' ? 'badge-activo' : 'badge-licencia'}>
                        {user.role === 'admin' ? 'Administrador' : user.role === 'jefe_local' ? 'Jefe de Local' : user.role === 'encargado_turno' ? 'Enc. Turno' : user.role === 'encargado_remuneraciones' ? 'Enc. Remuneraciones' : 'Encargado'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-600">
                      {user.role === 'admin' ? 'Todas' : user.role === 'encargado_remuneraciones' && user.allowed_branches?.length > 0
                        ? user.allowed_branches.map(bid => getBranchName(bid)).filter(Boolean).join(', ') || 'Sin acceso'
                        : getBranchName(user.branch_id)}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={user.is_active ? 'text-emerald-600' : 'text-zinc-400'}>
                        {user.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {user.role !== 'admin' && user.can_manage_bank_accounts && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-blue-50 text-blue-700 border border-blue-200" data-testid={`bank-perm-${user.id}`}>
                          <Bank size={12} /> Ctas. Bancarias
                        </span>
                      )}
                      {user.role !== 'admin' && user.can_manage_cierre_mes && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-amber-50 text-amber-700 border border-amber-200 ml-1" data-testid={`cierre-perm-${user.id}`}>
                          <Export size={12} /> Cierre de Mes
                        </span>
                      )}
                      {user.role === 'admin' && (
                        <span className="text-xs text-zinc-400">Todos</span>
                      )}
                    </td>
                    {canEditUsers && (
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleOpenModal(user)}
                          className="p-1 text-zinc-400 hover:text-zinc-900 transition-colors"
                          title="Editar usuario"
                          data-testid={`edit-user-${user.id}`}
                        >
                          <PencilSimple size={18} />
                        </button>
                        <button
                          onClick={() => {
                            setPasswordUser(user);
                            setNewPassword('');
                            setForceChange(true);
                            setError('');
                            setShowPasswordModal(true);
                          }}
                          className="p-1 text-zinc-400 hover:text-zinc-900 transition-colors"
                          title="Cambiar contraseña"
                          data-testid={`password-user-${user.id}`}
                        >
                          <Key size={18} />
                        </button>
                        <button
                          onClick={() => handleGenerateTempPassword(user)}
                          className="p-1 text-zinc-400 hover:text-amber-600 transition-colors"
                          title="Generar contraseña temporal"
                          data-testid={`temp-password-${user.id}`}
                        >
                          <ArrowClockwise size={18} />
                        </button>
                        {user.role !== 'admin' && (
                          <button
                            onClick={() => handleToggleActive(user)}
                            className={`text-xs ${user.is_active ? 'text-red-600' : 'text-emerald-600'}`}
                            data-testid={`toggle-user-${user.id}`}
                          >
                            {user.is_active ? 'Desactivar' : 'Activar'}
                          </button>
                        )}
                      </div>
                    </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* User Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-lg rounded-none border-zinc-200 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && <div className="alert-error text-sm">{error}</div>}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="form-label">Primer Nombre</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="form-input"
                  required
                  data-testid="user-firstname-input"
                />
              </div>
              <div>
                <label className="form-label">Segundo Nombre</label>
                <input
                  type="text"
                  value={formData.second_name}
                  onChange={(e) => setFormData({ ...formData, second_name: e.target.value })}
                  className="form-input"
                  required
                  data-testid="user-secondname-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="form-label">Primer Apellido</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="form-input"
                  required
                  data-testid="user-lastname-input"
                />
              </div>
              <div>
                <label className="form-label">Segundo Apellido</label>
                <input
                  type="text"
                  value={formData.second_last_name}
                  onChange={(e) => setFormData({ ...formData, second_last_name: e.target.value })}
                  className="form-input"
                  required
                  data-testid="user-secondlastname-input"
                />
              </div>
            </div>
            <div>
              <label className="form-label">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="form-input"
                required
                data-testid="user-email-input"
              />
            </div>
            {!editingUser && (
              <div>
                <label className="form-label">Contraseña</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="form-input"
                  required
                  minLength={6}
                  data-testid="user-password-input"
                />
              </div>
            )}
            <div>
              <label className="form-label">Rol</label>
              <select
                value={formData.role}
                onChange={(e) => handleRoleChange(e.target.value)}
                className="form-select"
                data-testid="user-role-select"
              >
                <option value="encargado">Encargado</option>
                <option value="jefe_local">Jefe de Local</option>
                <option value="encargado_turno">Encargado de Turno</option>
                <option value="encargado_remuneraciones">Encargado de Remuneraciones</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
            {formData.role !== 'admin' && (
              <div>
                <label className="form-label">Sucursal</label>
                <select
                  value={formData.branch_id}
                  onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
                  className="form-select"
                  required
                  data-testid="user-branch-select"
                >
                  <option value="">Seleccionar sucursal</option>
                  {branches.map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {formData.role !== 'admin' && (
              <div className="flex items-center gap-3 p-3 bg-zinc-50 border border-zinc-200" data-testid="bank-permission-section">
                <input
                  type="checkbox"
                  id="can_manage_bank_accounts"
                  checked={formData.can_manage_bank_accounts}
                  onChange={(e) => setFormData({ ...formData, can_manage_bank_accounts: e.target.checked })}
                  className="h-4 w-4 accent-zinc-900"
                  data-testid="bank-permission-toggle"
                />
                <label htmlFor="can_manage_bank_accounts" className="text-sm text-zinc-700 select-none">
                  <span className="font-medium">Puede gestionar cuentas bancarias</span>
                  <span className="block text-xs text-zinc-500 mt-0.5">
                    Permite ver y editar datos bancarios de trabajadores de su sucursal
                  </span>
                </label>
              </div>
            )}
            {formData.role !== 'admin' && (
              <div className="flex items-center gap-3 p-3 bg-zinc-50 border border-zinc-200" data-testid="cierre-permission-section">
                <input
                  type="checkbox"
                  id="can_manage_cierre_mes"
                  checked={formData.can_manage_cierre_mes}
                  onChange={(e) => setFormData({ ...formData, can_manage_cierre_mes: e.target.checked })}
                  className="h-4 w-4 accent-zinc-900"
                  data-testid="cierre-permission-toggle"
                />
                <label htmlFor="can_manage_cierre_mes" className="text-sm text-zinc-700 select-none">
                  <span className="font-medium">Puede hacer Doble Check y Cierre de Mes</span>
                  <span className="block text-xs text-zinc-500 mt-0.5">
                    Permite ver, editar y exportar el Cierre de Mes en Asistencia
                  </span>
                </label>
              </div>
            )}
            {formData.role === 'encargado_remuneraciones' && (
              <div className="p-3 bg-zinc-50 border border-zinc-200 space-y-2" data-testid="allowed-branches-section">
                <label className="text-sm font-medium text-zinc-700">Sucursales con acceso</label>
                <p className="text-xs text-zinc-500">Selecciona los locales que puede ver este usuario</p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {allActiveBranches.map(b => (
                    <label key={b.id} className="flex items-center gap-2 text-sm text-zinc-700 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={formData.allowed_branches?.includes(b.id) || false}
                        onChange={(e) => {
                          const current = formData.allowed_branches || [];
                          const updated = e.target.checked
                            ? [...current, b.id]
                            : current.filter(id => id !== b.id);
                          setFormData({ ...formData, allowed_branches: updated });
                        }}
                        className="h-4 w-4 accent-zinc-900"
                        data-testid={`branch-access-${b.id}`}
                      />
                      {b.name}
                    </label>
                  ))}
                </div>
                {allActiveBranches.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, allowed_branches: allActiveBranches.map(b => b.id) })}
                    className="text-[10px] text-blue-600 hover:underline mt-1"
                    data-testid="select-all-branches"
                  >
                    Seleccionar todos
                  </button>
                )}
              </div>
            )}
            {/* Module Permissions */}
            <ModulePermissionsEditor
              permissions={formData.module_permissions || {}}
              onChange={handlePermissionsChange}
              isAdminRole={formData.role === 'admin'}
              isNewUser={!editingUser}
              isTemplateApplied={isTemplateApplied}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="rounded-none"
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={saving} className="rounded-none bg-zinc-900">
                {saving ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Password Modal */}
      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="sm:max-w-md rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading">
              Cambiar Contraseña
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {error && <div className="alert-error text-sm">{error}</div>}
            <p className="text-sm text-zinc-600">
              Cambiar contraseña de <strong>{passwordUser?.name}</strong> ({passwordUser?.email})
            </p>
            <div>
              <label className="form-label">Nueva Contraseña</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="form-input"
                minLength={6}
                placeholder="Mínimo 6 caracteres"
                data-testid="new-password-input"
              />
            </div>
            <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200">
              <input
                type="checkbox"
                id="forceChange"
                checked={forceChange}
                onChange={(e) => setForceChange(e.target.checked)}
                className="h-4 w-4 accent-amber-600"
                data-testid="force-change-checkbox"
              />
              <label htmlFor="forceChange" className="text-sm text-amber-800 cursor-pointer">
                Forzar cambio de contraseña en el próximo inicio de sesión
              </label>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPasswordModal(false)}
                className="rounded-none"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleChangePassword}
                disabled={saving}
                className="rounded-none bg-zinc-900"
                data-testid="save-password-btn"
              >
                {saving ? 'Guardando...' : 'Cambiar'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Temp Password Result Modal */}
      <Dialog open={showTempPasswordModal} onOpenChange={setShowTempPasswordModal}>
        <DialogContent className="sm:max-w-md rounded-none border-zinc-200">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Check size={20} className="text-emerald-600" />
              Contraseña Temporal Generada
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-zinc-50 border border-zinc-200">
              <p className="text-xs text-zinc-500 mb-1">Usuario</p>
              <p className="text-sm font-medium text-zinc-900" data-testid="temp-email">{tempPasswordResult?.email}</p>
            </div>
            <div className="p-4 bg-emerald-50 border border-emerald-200">
              <p className="text-xs text-emerald-600 mb-1">Contraseña Temporal</p>
              <div className="flex items-center gap-2">
                <code className="text-lg font-mono font-bold text-emerald-800 flex-1" data-testid="temp-password-value">
                  {tempPasswordResult?.temp_password}
                </code>
                <button
                  onClick={() => copyToClipboard(tempPasswordResult?.temp_password)}
                  className="p-2 hover:bg-emerald-100 transition-colors"
                  title="Copiar"
                  data-testid="copy-temp-password-btn"
                >
                  <Copy size={18} className="text-emerald-600" />
                </button>
              </div>
            </div>
            <div className="p-3 bg-amber-50 border border-amber-200 flex items-start gap-2">
              <Warning size={18} className="text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-amber-800">
                El usuario deberá cambiar esta contraseña en su próximo inicio de sesión. Comparta esta contraseña de forma segura.
              </p>
            </div>
            <DialogFooter>
              <Button
                onClick={() => {
                  setShowTempPasswordModal(false);
                  setTempPasswordResult(null);
                }}
                className="rounded-none bg-zinc-900"
                data-testid="close-temp-modal-btn"
              >
                Cerrar
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
